<template>
  <v-container class="insights">
    <template v-if="!loading.view && allFacilitiesIds.length === 0">
      <v-row>
        <ViewHeader :title="viewTitle"> </ViewHeader>
        <v-col cols="12">
          <v-card style="min-height: 650px;" class="d-flex">
            <div class="mx-auto d-flex align-center">
              <div class="no-data d-flex flex-column align-center">
                <h2 class="subtitle mb-6">
                  {{ $t("message.common.noData") }}
                </h2>
                <p class="no-data__description text-center">
                  {{ $t("message.analytics.businessAddresses.noData") }}
                </p>
                <v-btn color="secondary" @click="goToGMBAccounts">
                  {{ $t("message.common.addAccount") }}
                </v-btn>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </template>
    <template v-if="allFacilitiesIds.length > 0">
      <v-row justify="center" no-gutters>
        <v-col cols="12">
          <ViewHeader :title="viewTitle">
            <div class="header-actions">
              <div class="mr-4">
                <v-tooltip bottom :max-width="tooltipMaxWidth">
                  <template v-slot:activator="{ on }">
                    <div v-on="on">
                      <v-icon :color="infoIconColor" size="17.5">
                        mdi-information
                      </v-icon>
                    </div>
                  </template>
                  {{ $t("message.analytics.businessAddresses.titleTooltip") }}
                </v-tooltip>
              </div>

              <v-menu
                ref="menu"
                v-model="menu"
                :close-on-content-click="false"
                :return-value.sync="date"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :hide-details="true"
                    :value="dateRangeText"
                    :label="$t('message.common.chooseDate')"
                    prepend-icon="icon-calendar"
                    readonly
                    v-on="on"
                    class="ml-auto mr-8"
                    style="min-width: 230px;"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="date"
                  first-day-of-week="1"
                  :locale="selectedLocale"
                  :allowed-dates="getAllowedDays"
                  no-title
                  scrollable
                  range
                  color="primary"
                  class="pa-2"
                >
                  <div class="full-width">
                    <div class="dates-select">
                      <v-text-field
                        v-model="dateFrom"
                        @keyup.enter="handleEditDatesBtn"
                        :label="$t('message.common.dateFrom')"
                        :disabled="!editDates"
                      ></v-text-field
                      ><v-text-field
                        v-model="dateTo"
                        @keyup.enter="handleEditDatesBtn"
                        :label="$t('message.common.dateTo')"
                        :disabled="!editDates"
                      ></v-text-field>
                      <v-btn icon color="primary" @click="handleEditDatesBtn">
                        <v-icon>
                          {{ editDates ? "icon-check" : "icon-edit" }}
                        </v-icon>
                      </v-btn>
                    </div>
                    <div class="compare-select">
                      <span class="mr-2 text-no-wrap mb-3">{{
                        $t("message.common.compareWith")
                      }}</span>
                      <v-select
                        :items="comparePeriodItems"
                        v-model="comparePeriodValue"
                        dense
                      ></v-select>
                    </div>
                    <div class="d-flex justify-space-between">
                      <ui-button outlined color="primary" @click="menu = false">
                        {{ $t("message.common.cancel") }}
                      </ui-button>
                      <v-spacer />
                      <ui-button color="primary" @click="handleDatePickerOk">
                        {{ $t("message.common.ok") }}
                      </ui-button>
                    </div>
                  </div>
                </v-date-picker>
              </v-menu>
              <div class="csv-export">
                <p>{{ $t("message.common.exportTo") }}</p>
                <v-menu
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset-y
                >
                  <template v-slot:activator="{ on }">
                    <ui-button
                      v-on="on"
                      color="primary"
                      :loading="loading.exportBtn"
                      append-icon="icon-arrow-down"
                    >
                      {{ $t("message.common.csv") }}
                    </ui-button>
                  </template>
                  <v-list class="csv-export__list">
                    <v-list-item
                      class="csv-export__list-item"
                      @click="exportGMBAddressesData(EXPORT_FILE_EXTENSION.CSV)"
                    >
                      {{ $t("message.common.exportGroupedByDates") }}
                    </v-list-item>
                    <v-list-item
                      class="csv-export__list-item"
                      @click="
                        exportGMBAddressesData(
                          EXPORT_FILE_EXTENSION.CSV,
                          GMB_EXPORT_GROUP_BY.GMB_ADDRESS
                        )
                      "
                    >
                      {{ $t("message.common.exportGroupedByLocations") }}
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
              <ui-button
                color="primary"
                @click.native.stop="filterDialog = true"
                icon="icon-filter"
              >
                {{ $t("message.common.filter") }}
              </ui-button>
            </div>
          </ViewHeader>
        </v-col>
      </v-row>
      <v-divider />
      <div v-if="!loading.view">
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle mb-2" id="queriesCount">
                  {{
                    $t("message.analytics.businessAddresses.subtitle.search")
                  }}
                </h3>
              </v-col>
            </v-row>
            <v-row class="d-flex">
              <v-col cols="4">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.directDiscoveryBranded.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.directDiscoveryBranded.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.directDiscoveryBranded.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                  </div>
                  <div class="pa-0 pa-lg-2 pa-xl-8" v-if="charts">
                    <DoughnutChart
                      :data="charts.directDiscoveryBranded.data"
                      :labels="charts.directDiscoveryBranded.labelsText"
                      :options="charts.directDiscoveryBranded.options"
                      :colors="charts.directDiscoveryBranded.colors"
                    />
                    <div class="labels">
                      <div
                        v-for="(label, index) in charts.directDiscoveryBranded
                          .labels"
                        :key="`label-1-${index}`"
                        class="label"
                      >
                        <span
                          class="label-color"
                          :style="
                            `backgroundColor: ${charts.directDiscoveryBranded.colors[index]}`
                          "
                        ></span>
                        <span class="label-text">{{ label.text }}</span>
                        <v-tooltip top :max-width="tooltipMaxWidth">
                          <template v-slot:activator="{ on }">
                            <div class="info-icon" v-on="on">
                              <v-icon :color="infoIconColor" size="17.5">
                                mdi-information
                              </v-icon>
                            </div>
                          </template>
                          {{ label.tooltip }}
                        </v-tooltip>
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
              <v-col cols="8">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.searches.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.searches.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.searches.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                    <v-spacer />
                    <span>{{ charts.searches.sum }}</span>
                    <LastAudit
                      v-if="charts.searches.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.searches.percentage"
                    />
                  </div>
                  <BarChart
                    :data="charts.searches.data"
                    :labels="charts.searches.dataSetsLabels"
                    :options="charts.searches.options"
                  />
                  <div class="labels">
                    <div
                      v-for="(label, index) in charts.directDiscoveryBranded
                        .labels"
                      :key="`label-1-${index}`"
                      class="label"
                    >
                      <span
                        class="label-color"
                        :style="
                          `backgroundColor: ${charts.directDiscoveryBranded.colors[index]}`
                        "
                      ></span>
                      <span class="label-text">{{ label.text }}</span>
                      <v-tooltip top :max-width="tooltipMaxWidth">
                        <template v-slot:activator="{ on }">
                          <div class="info-icon" v-on="on">
                            <v-icon :color="infoIconColor" size="17.5">
                              mdi-information
                            </v-icon>
                          </div>
                        </template>
                        {{ label.tooltip }}
                      </v-tooltip>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle mb-2 mt-6" id="viewsCount">
                  {{
                    $t("message.analytics.businessAddresses.subtitle.displays")
                  }}
                </h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.searchMaps.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.searchMaps.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.searchMaps.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                  </div>
                  <div class="pa-0 pa-lg-2 pa-xl-8">
                    <DoughnutChart
                      :data="charts.searchMaps.data"
                      :labels="charts.searchMaps.labels"
                      :options="charts.searchMaps.options"
                      :colors="charts.searchMaps.colors"
                    />
                    <div class="labels">
                      <div
                        v-for="(label, index) in charts.searchMaps.labels"
                        :key="`label-${index}`"
                        class="label"
                      >
                        <span
                          class="label-color"
                          :style="
                            `backgroundColor: ${charts.searchMaps.colors[index]}`
                          "
                        ></span>
                        <span class="label-text">{{ label }}</span>
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
              <v-col cols="8">
                <v-card
                  min-height="200"
                  class="fill-height pa-8"
                  v-if="charts && charts.maps.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.maps.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.maps.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                    <v-spacer />
                    <span>{{ charts.maps.sum }}</span>
                    <LastAudit
                      v-if="charts.maps.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.maps.percentage"
                    />
                  </div>
                  <BarChart
                    :data="charts.maps.data"
                    :labels="charts.maps.dataSetsLabels"
                    :options="charts.maps.options"
                  />
                  <div class="labels">
                    <div
                      v-for="(label, index) in charts.searchMaps.labels"
                      :key="`label-${index}`"
                      class="label"
                    >
                      <span
                        class="label-color"
                        :style="
                          `backgroundColor: ${charts.searchMaps.colors[index]}`
                        "
                      ></span>
                      <span class="label-text">{{ label }}</span>
                    </div>
                  </div>
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle mb-0" id="actions">
                  {{
                    $t("message.analytics.businessAddresses.subtitle.actions")
                  }}
                </h3>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" xl="4">
                <v-card
                  id="actionsWebsite"
                  min-height="350"
                  class="pa-8"
                  v-if="charts && charts.clicks.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.clicks.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.clicks.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                    <v-spacer />
                    <span>{{ charts.clicks.sum }}</span>
                    <LastAudit
                      v-if="charts.clicks.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.clicks.percentage"
                    />
                  </div>
                  <LineChart
                    :height="300"
                    :data="charts.clicks.data"
                    :options="charts.clicks.options"
                  />
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
              <v-col cols="12" xl="4">
                <v-card
                  id="actionsDrivingDirections"
                  min-height="350"
                  class="pa-8"
                  v-if="charts && charts.directions.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.directions.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.directions.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                    <v-spacer />
                    <span>{{ charts.directions.sum }}</span>
                    <LastAudit
                      v-if="charts.directions.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.directions.percentage"
                    />
                  </div>
                  <LineChart
                    :height="300"
                    :data="charts.directions.data"
                    :options="charts.directions.options"
                  />
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
              <v-col cols="12" xl="4">
                <v-card
                  id="actionsPhone"
                  min-height="350"
                  class="pa-8"
                  v-if="charts && charts.calls.sum !== '0'"
                >
                  <div class="d-flex">
                    <h4 class="chart-title">
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.calls.title.text"
                        )
                      }}
                    </h4>
                    <v-tooltip top :max-width="tooltipMaxWidth">
                      <template v-slot:activator="{ on }">
                        <div class="info-icon" v-on="on">
                          <v-icon :color="infoIconColor" size="17.5">
                            mdi-information
                          </v-icon>
                        </div>
                      </template>
                      {{
                        $t(
                          "message.analytics.businessAddresses.chart.calls.title.tooltip"
                        )
                      }}
                    </v-tooltip>
                    <v-spacer />
                    <span>{{ charts.calls.sum }}</span>
                    <LastAudit
                      v-if="charts.calls.percentage !== null"
                      class="ml-3"
                      hideTitle
                      showZero
                      :lastAudit="charts.calls.percentage"
                    />
                  </div>
                  <LineChart
                    :height="300"
                    :data="charts.calls.data"
                    :options="charts.calls.options"
                  />
                </v-card>
                <v-card
                  min-height="200"
                  class="d-flex justify-center align-center fill-height pa-8"
                  v-else
                >
                  <no-data
                    :description="$t('message.common.noDataDescription')"
                  ></no-data>
                </v-card>
              </v-col>
            </v-row>
          </v-col>

          <FilterDialog
            v-if="filterDialog"
            :gmb-addresses-ids="gmbAddressesIds"
            :cities="cities"
            :facilities-groups="facilitiesGroupsIds"
            :chosen-search-type="searchType"
            @setGmbAddressesIds="gmbAddressesIds = $event"
            @setCities="cities = $event"
            @setFacilitiesGroupsIds="facilitiesGroupsIds = $event"
            @setSearchType="searchType = $event"
            @change="onFiltersChange({ ...$event })"
            :handleClose="closeFilterDialog"
          />
        </v-row>
      </div>
    </template>
    <SectionLoader :status="loading.view" :opacity="1" />
  </v-container>
</template>

<script>
import { GMBAddressesFacade } from "@/utils/google/GMB";
import { gmbAddressMixin } from "@/helpers/mixins/gmb-address";
import { gtmEngagement as gtmEngagementMixin } from "@/helpers/mixins/gtm-engagement";
import { scrollToHash } from "@/helpers/mixins/scroll-to-hash";
import { FORMAT } from "@/config/date-format";
import moment from "moment";
import { PERIOD } from "@/enums/period.enum";

export default {
  name: "GMBAnalyticsViewArchive",
  mixins: [scrollToHash, gtmEngagementMixin, gmbAddressMixin],
  data() {
    return {
      dateFrom: moment({ month: 1, year: 2023 })
        .startOf("month")
        .format(FORMAT.DATE),
      dateTo: moment({ month: 1, year: 2023 })
        .endOf("month")
        .format(FORMAT.DATE),
      date: null
    };
  },
  created() {
    this.date = [this.dateFrom, this.dateTo];

    this.gmbAddressesFacade = new GMBAddressesFacade();
  },
  methods: {
    getAllowedDays(val) {
      const dateTo = moment({ month: 1, year: 2023 })
        .endOf("month")
        .format(FORMAT.DATE);
      const compareDate = moment(val);

      return !compareDate.isAfter(dateTo, "days");
    }
  }
};
</script>

<style lang="scss" scoped src="./GMBAddress.scss"></style>
